import React, { useContext, useEffect, useRef } from 'react';
import { useEventListener, useDebouncedCallback, useAdsData } from '@24i/player-utils-react';
import { PlayerUIContext, relativeSeekCompleted } from '@24i/player-ui';
import { Button } from '../Button/styled';
import { StyledIcon as PlayerIcon } from '../Icons';
import { handleSeek } from '../PlayerUIContainer';
const DEFAULT_SEEK_DELAY = 500;
const HOLD_TIMER = 200;
export var Sign;
(function (Sign) {
    Sign[Sign["Positive"] = 0] = "Positive";
    Sign[Sign["Negative"] = 1] = "Negative";
})(Sign || (Sign = {}));
const EDGE_TOLERANCE = 500;
function isAtEdge(playerInstance, sign) {
    if (!playerInstance)
        return true;
    const { seekableRange } = playerInstance;
    if (!seekableRange)
        return true;
    if (sign === Sign.Positive) {
        if (playerInstance.live)
            return playerInstance.isOnLiveEdge();
        return playerInstance.currentTime >= seekableRange.end - EDGE_TOLERANCE;
    }
    return playerInstance.currentTime <= seekableRange.start + EDGE_TOLERANCE;
}
export const SeekRelativeButton = ({ playerInstance, amount, sign, backgroundColor, className, customIcon, styles, disabled = false, variant, }) => {
    const { state: { relativeSeekValue }, dispatch, } = useContext(PlayerUIContext);
    const button = useRef(null);
    const multiplierInterval = useRef(null);
    const { isSeekingBackwardDisabled, isSeekingForwardDisabled } = useAdsData(playerInstance);
    const isSeekingDisabled = sign === Sign.Positive ? isSeekingForwardDisabled : isSeekingBackwardDisabled;
    const finalDisabled = disabled || isSeekingDisabled || isAtEdge(playerInstance, sign);
    const performSeek = () => {
        if (!relativeSeekValue)
            return;
        if (sign === Sign.Negative) {
            if (relativeSeekValue < 0) {
                playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.backward(Math.abs(relativeSeekValue));
                dispatch(relativeSeekCompleted());
            }
        }
        else if (relativeSeekValue > 0) {
            playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.forward(Math.abs(relativeSeekValue));
            dispatch(relativeSeekCompleted());
        }
    };
    const [debouncedSeek] = useDebouncedCallback(performSeek, DEFAULT_SEEK_DELAY);
    useEffect(() => {
        debouncedSeek();
    }, [relativeSeekValue]);
    const onKeyPress = (event) => {
        event.preventDefault();
    };
    useEventListener('keydown', onKeyPress, button.current);
    useEventListener('keyup', onKeyPress, button.current);
    const onClickHandler = (event) => {
        if (finalDisabled)
            return;
        handleSeek(playerInstance, sign === Sign.Negative ? -amount : amount);
        if (event)
            event.stopPropagation();
    };
    const handleAccumulativePress = () => {
        if (finalDisabled)
            return;
        clearInterval(multiplierInterval.current);
        onClickHandler(null);
        multiplierInterval.current = setInterval(onClickHandler, HOLD_TIMER);
    };
    const handleAccumulativeRelease = () => {
        clearInterval(multiplierInterval.current);
        multiplierInterval.current = null;
    };
    const isPodcastPlayer = variant === 'podcast';
    const rewindIcon = isPodcastPlayer ? 'onPrimary-control_replay10' : 'onDark-control_replay10';
    const forwardIcon = isPodcastPlayer
        ? 'onPrimary-control_forward10'
        : 'onDark-control_forward10';
    const iconName = sign === Sign.Negative ? rewindIcon : forwardIcon;
    const stopPropagation = (event) => {
        event.stopPropagation();
        event.preventDefault();
    };
    return (React.createElement(Button, { ref: button, style: styles.button, "data-test": "SeekRelativeButton-Button", testID: `${sign === Sign.Negative ? 'rewind' : 'forward'}_button`, className: `${className} SeekRelativeButton-Button`, onClick: finalDisabled ? stopPropagation : onClickHandler, onDoubleClick: stopPropagation, onMouseDown: handleAccumulativePress, onMouseUp: handleAccumulativeRelease, onMouseLeave: handleAccumulativeRelease, onTouchStart: handleAccumulativePress, onTouchEnd: handleAccumulativeRelease, backgroundColor, finalDisabled }, customIcon(iconName, backgroundColor, styles, finalDisabled)));
};
const RenderDefaultIcon = (buttonIconName, backgroundColor, styles, disabled) => (React.createElement(PlayerIcon, { name: buttonIconName, style: Object.assign(Object.assign({}, styles.icon), { opacity: disabled ? 0.4 : 1 }), "data-test": "SeekRelativeButton-Icon", className: "SeekRelativeButton-Icon", backgroundColor }));
SeekRelativeButton.defaultProps = {
    className: '',
    color: '',
    backgroundColor: 'transparent',
    amount: 10000,
    customIcon: RenderDefaultIcon,
    styles: {
        button: null,
        icon: null,
    },
};
//# sourceMappingURL=index.js.map